import React, { useMemo } from "react";
import {
  PricingCard as PricingCardBase,
  PriceLockup,
  Button,
  NumericInput,
} from "@sparkol/videoscribe-react-ui";
import { useStore } from "@nanostores/react";
import { selectedPricingTerm } from "../../../store";
import { getCurrencyFormatter } from "../../../lib/lang";
import Cookie from "js-cookie";
import {
  calculateVolumeAndUnitPrice,
  type ProductAndPricing,
} from "../../../lib/fastspring";

interface Props {
  title: string;
  id: string;
  children?: React.ReactNode;
  locale: string;
  currency: string;
  selectedTerm: string;
  maxLicenses: number;
  allowMultipleLicenses?: boolean;
  pricingData: {
    countryCode: string;
    monthly: ProductAndPricing;
    annual: ProductAndPricing;
  };
  qty?: number;
  callout?: string;
  variant?: "dark" | "light";
}

const PricingCard = ({
  title,
  id,
  children,
  pricingData,
  locale,
  currency,
  qty = 1,
  maxLicenses,
  allowMultipleLicenses = false,
  callout,
  variant = "light",
  selectedTerm,
}: Props) => {
  const [quantity, setQuantity] = React.useState(
    allowMultipleLicenses ? qty : 1,
  );
  const cardRef = React.useRef<HTMLDivElement>(null);
  const term = useStore(selectedPricingTerm) || selectedTerm;
  const isAnnual = term === "annual";
  const productPath = isAnnual
    ? pricingData.annual.product
    : pricingData.monthly.product;

  const formatter = useMemo(() => {
    return getCurrencyFormatter(locale, currency);
  }, [locale, currency]);

  const { unitPrice: monthlyUnitPrice, volumePrice: monthlyVolumePrice } =
    calculateVolumeAndUnitPrice(
      pricingData.monthly.pricing[pricingData.countryCode],
      quantity,
    );
  const { unitPrice: annualUnitPrice, volumePrice: annualVolumePrice } =
    calculateVolumeAndUnitPrice(
      pricingData.annual.pricing[pricingData.countryCode],
      quantity,
    );

  const annualDisplayPrice = formatter.format(annualUnitPrice / 12);
  const monthlyDisplayPrice = formatter.format(monthlyUnitPrice);
  const displayPrice = isAnnual ? annualDisplayPrice : monthlyDisplayPrice;
  const annualByline = `${formatter.format(annualVolumePrice)} billed annually`;
  const monthlyByline =
    quantity > 1
      ? `${formatter.format(monthlyVolumePrice)} billed monthly`
      : `billed monthly`;
  const byline = isAnnual ? annualByline : monthlyByline;

  const handleButtonClick = () => {
    if (cardRef.current) {
      const quantityToBuy = allowMultipleLicenses ? quantity : 1;
      Cookie.set(`${productPath}-quantity`, quantityToBuy.toString());

      cardRef.current.dispatchEvent(
        new CustomEvent("checkout", {
          detail: {
            product: productPath,
          },
        }),
      );
    }
  };

  const buttonVariant = variant === "dark" ? "primaryLightHover" : "primary";
  return (
    <PricingCardBase
      id={id}
      title={title}
      ref={cardRef}
      callout={callout}
      variant={variant}
    >
      <div className="PricingCard__top">
        <PriceLockup
          price={displayPrice}
          byline={byline}
          sub="/month per license"
        />
        {allowMultipleLicenses && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <NumericInput
              id={`${id}-numeric-input`}
              value={quantity}
              onChange={(val: number) => setQuantity(val)}
              max={100}
              min={1}
            />
            <label htmlFor={`${id}-numeric-input`}>licenses</label>
          </div>
        )}
      </div>
      {quantity > maxLicenses ? (
        <Button href="#form" variant={buttonVariant}>
          Contact us to purchase
        </Button>
      ) : (
        <Button onClick={handleButtonClick} variant={buttonVariant}>
          Buy now
        </Button>
      )}
      {children}
    </PricingCardBase>
  );
};

export default PricingCard;
